import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Package, CheckCircle, Users, AlertTriangle, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { formatDisplayDate } from '@/lib/utils';

const StatCard = ({ title, value, icon: Icon, color }) => {
  // Ensure value is always a number, defaulting to 0
  const displayValue = typeof value === 'number' ? value : (Number(value) || 0);
  
  return (
      <div
        data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}
        className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 hover-lift"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-medium text-slate-600">{title}</p>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold mt-2 tracking-tight text-[#0B1F3A]">{displayValue}</p>
          </div>
          <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-xl flex items-center justify-center flex-shrink-0 ml-2 ${color}`}>
            <Icon className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
          </div>
        </div>
      </div>
  );
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_assets: 0,
    assigned_assets: 0,
    available_assets: 0,
    total_employees: 0,
    under_repair_assets: 0,
    active_employees: 0,
    exited_employees: 0,
      total_assignments: 0,
      active_assignments: 0,
      total_sims: 0,
      assigned_sims: 0,
      available_sims: 0,
    });
  const [pendingReturns, setPendingReturns] = useState([]);
  const [pendingSimReturns, setPendingSimReturns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Add cache-busting timestamp to ensure fresh data
        const timestamp = new Date().getTime();
        const [statsRes, pendingRes, pendingSimRes] = await Promise.all([
          api.get('/dashboard/stats', {
            params: { _t: timestamp },
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache',
            },
          }),
          api.get('/pending-returns', {
            params: { _t: timestamp },
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache',
            },
          }).catch(() => ({ data: [] })), // Silently handle pending returns errors
          api.get('/dashboard/pending-sim-returns', {
            params: { _t: timestamp },
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache',
            },
          }).catch(() => ({ data: [] })), // Silently handle pending SIM returns errors
        ]);
        
        // Handle response - axios wraps the response in .data
        const statsData = statsRes.data || {};
        
        // Ensure all stats fields are set, converting to numbers
        const updatedStats = {
          total_assets: Number(statsData.total_assets) || 0,
          assigned_assets: Number(statsData.assigned_assets) || 0,
          available_assets: Number(statsData.available_assets) || 0,
          total_employees: Number(statsData.total_employees) || 0,
          under_repair_assets: Number(statsData.under_repair_assets) || 0,
          active_employees: Number(statsData.active_employees) || 0,
          exited_employees: Number(statsData.exited_employees) || 0,
          total_assignments: Number(statsData.total_assignments) || 0,
          active_assignments: Number(statsData.active_assignments) || 0,
          total_sims: Number(statsData.total_sims) || 0,
          assigned_sims: Number(statsData.assigned_sims) || 0,
          available_sims: Number(statsData.available_sims) || 0,
        };
        
        setStats(updatedStats);
        
        // Combine pending asset returns and SIM returns by employee
        const pendingAssets = pendingRes.data || [];
        const pendingSims = pendingSimRes.data || [];
        
        // Group SIMs by employee_id
        const simsByEmployee = {};
        pendingSims.forEach(sim => {
          if (!simsByEmployee[sim.employee_id]) {
            simsByEmployee[sim.employee_id] = {
              employee_id: sim.employee_id,
              employee_name: sim.employee_name || 'Unknown',
              email: sim.employee_email || '',
              sims: []
            };
          }
          simsByEmployee[sim.employee_id].sims.push({
            id: sim.id,
            mobile_number: sim.mobile_number,
            sim_provider: sim.sim_provider,
            sim_type: sim.sim_type,
            asset_id: sim.asset_id,
            asset_name: sim.asset_name,
            assign_date: sim.assign_date,
            purpose: sim.purpose
          });
        });
        
        // Merge assets and SIMs by employee_id
        const combined = {};
        
        // Add employees with pending assets
        pendingAssets.forEach(item => {
          combined[item.employee_id] = {
            employee_id: item.employee_id,
            employee_name: item.employee_name,
            email: item.email,
            assets: item.assets || [],
            sims: simsByEmployee[item.employee_id]?.sims || []
          };
        });
        
        // Add employees with pending SIMs but no assets
        Object.values(simsByEmployee).forEach(emp => {
          if (!combined[emp.employee_id]) {
            combined[emp.employee_id] = {
              employee_id: emp.employee_id,
              employee_name: emp.employee_name,
              email: emp.email,
              assets: [],
              sims: emp.sims
            };
          }
        });
        
        setPendingReturns(Object.values(combined));
        setPendingSimReturns([]); // Keep for compatibility but won't render separately
      } catch (error) {
        console.error('Dashboard fetch error:', error);
        toast.error('Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B1F3A]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B1F3A]">Dashboard</h1>
        <p className="text-slate-600 mt-2 text-sm sm:text-base">Overview of your asset management system</p>
      </div>

      {/* Row 1: Totals only */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          title="Total Employees"
          value={stats.total_employees ?? 0}
          icon={Users}
          color="bg-blue-500"
        />
        <StatCard
          title="Total Assets"
          value={stats.total_assets ?? 0}
          icon={Package}
          color="bg-[#0B1F3A]"
        />
        <StatCard
          title="Total SIMs"
          value={stats.total_sims ?? 0}
          icon={Smartphone}
          color="bg-indigo-500"
        />
      </div>

      {/* Row 2: Active employee + Assigned details */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          title="Active Employees"
          value={stats.active_employees ?? 0}
          icon={Users}
          color="bg-blue-600"
        />
        <StatCard
          title="Assigned Assets"
          value={stats.assigned_assets ?? 0}
          icon={CheckCircle}
          color="bg-[#D81B60]"
        />
        <StatCard
          title="Assigned SIMs"
          value={stats.assigned_sims ?? 0}
          icon={Smartphone}
          color="bg-violet-500"
        />
      </div>

      {pendingReturns.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-4 sm:p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-orange-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h2 className="text-xl sm:text-2xl font-bold text-[#0B1F3A]">Pending Asset and SIM Returns</h2>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  Exit employees with unreturned assets and SIM cards ({pendingReturns.length})
                </p>
              </div>
            </div>
          </div>

          <div className="divide-y divide-slate-200">
            {pendingReturns.map((item) => {
              const totalItems = (item.assets?.length || 0) + (item.sims?.length || 0);
              return (
                <div
                  key={item.employee_id}
                  data-testid={`pending-return-${item.employee_id}`}
                  className="p-4 sm:p-6 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-[#0B1F3A] break-words">{item.employee_name}</h3>
                      <p className="text-xs sm:text-sm text-slate-600 mt-1 break-words">
                        {item.employee_id} • {item.email}
                      </p>
                      
                      {/* Unreturned Assets */}
                      {item.assets && item.assets.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs sm:text-sm font-medium text-slate-700 mb-2">
                            Unreturned Assets ({item.assets.length}):
                          </p>
                          <div className="space-y-2">
                            {item.assets.map((asset) => (
                              <div
                                key={asset.assignment_id}
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate('/assignments', { state: { openAssignmentId: asset.assignment_id } })}
                                onKeyDown={(e) => e.key === 'Enter' && navigate('/assignments', { state: { openAssignmentId: asset.assignment_id } })}
                                className="bg-orange-50 border border-orange-200 rounded-lg p-2 sm:p-3 text-xs sm:text-sm hover:bg-orange-100 hover:border-orange-300 cursor-pointer transition-colors"
                              >
                                <p className="font-medium text-orange-900 break-words">{asset.asset_name}</p>
                                <p className="text-orange-700 text-xs mt-1 break-words">
                                  Asset ID: {asset.asset_id} • Assigned: {asset.assigned_date}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Unreturned SIMs */}
                      {item.sims && item.sims.length > 0 && (
                        <div className={`mt-4 ${item.assets && item.assets.length > 0 ? 'pt-4 border-t border-slate-200' : ''}`}>
                          <p className="text-xs sm:text-sm font-medium text-slate-700 mb-2">
                            Unreturned SIM Cards ({item.sims.length}):
                          </p>
                          <div className="space-y-2">
                            {item.sims.map((sim) => (
                              <div
                                key={sim.id}
                                role="button"
                                tabIndex={0}
                                //onClick={() => navigate('/sim-connections', { state: { openSimId: Number(sim.id) } })}
                                onDoubleClick={() => navigate('/sim-connections', { state: { openSimId: Number(sim.id) } })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    navigate('/sim-connections', { state: { openSimId: Number(sim.id) } });
                                  }
                                }}
                                className="bg-orange-50 border border-orange-200 rounded-lg p-2 sm:p-3 text-xs sm:text-sm hover:bg-orange-100 hover:border-orange-300 cursor-pointer transition-colors"
                              >
                                <p className="font-medium text-orange-900 break-words">
                                  {sim.mobile_number} ({sim.sim_provider})
                                </p>
                                <p className="text-orange-700 text-xs mt-1 break-words">
                                  {sim.sim_type}
                                  {sim.asset_name && ` • Asset: ${sim.asset_name} (${sim.asset_id})`}
                                  {sim.assign_date && ` • Assigned: ${formatDisplayDate(sim.assign_date)}`}
                                </p>
                                {sim.purpose && (
                                  <p className="text-orange-600 text-xs mt-1">
                                    Purpose: {sim.purpose}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="sm:ml-4 flex-shrink-0">
                      <span className="inline-block px-2 sm:px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full whitespace-nowrap">
                        Action Required
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}